/** Additive list/navigation generation fence. Old API payloads remain compatible. */

import { applyFixturePatchToFixtureList } from './sportsnewListPatch.js';
import { isSportsnewFixtureVisibleForMode } from './sportsnewListFixtureVisibility.js';

export const normalizeSportsnewMembershipRevision = (payload) => {
  const row = Array.isArray(payload)
    ? payload[0]
    : Array.isArray(payload?.data) ? payload.data[0] : null;
  const value = payload?.membershipRevision
    ?? payload?.membership_revision
    ?? payload?.data?.membershipRevision
    ?? payload?.data?.membership_revision
    ?? row?.membershipRevision
    ?? row?.membership_revision;
  if (value == null || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
};

export const sportsnewMembershipRevisionsCompatible = (left, right) => {
  const listRevision = normalizeSportsnewMembershipRevision({ membershipRevision: left });
  const navigationRevision = normalizeSportsnewMembershipRevision({ membershipRevision: right });
  return listRevision == null
    || navigationRevision == null
    || listRevision === navigationRevision;
};

const AUTHORITATIVE_MEMBERSHIP_EVENT_TYPES = new Set([
  'FIXTURE_ADDED',
  'FIXTURE_REMOVED',
  'FIXTURE_VISIBILITY',
  'FIXTURE_SCHEDULE',
  'FIXTURE_TERMINAL',
  'FIXTURE_LIST_MEMBERSHIP',
  'LIST_MEMBERSHIP',
  'PUBLIC_VISIBILITY_REMOVED',
]);

const FAIL_CLOSED_HOLD_EVENT_TYPES = new Set([
  'FIXTURE_REMOVED',
  'FIXTURE_TERMINAL',
  'PUBLIC_VISIBILITY_REMOVED',
]);

const REOPEN_AUTHORITY_EVENT_TYPES = new Set([
  'FIXTURE_ADDED',
  'FIXTURE_VISIBILITY',
]);

const TERMINAL_FIXTURE_STATUSES = new Set([
  'FINAL',
  'COMPLETED',
  'ENDED',
  'END',
  'FINISHED',
  'CANCELLED',
  'CANCELED',
  'ABANDONED',
  'SETTLED',
  'CLOSED',
]);

const normalizedEventTypes = (patch = {}) => (
  Array.isArray(patch?.eventTypes)
    ? patch.eventTypes
    : Array.isArray(patch?.event_types) ? patch.event_types : []
).map(value => String(value ?? '').trim().toUpperCase());

export const sportsnewListPatchHasMembershipTransition = (patch = {}) => {
  const eventTypes = normalizedEventTypes(patch);
  return eventTypes.some(eventType => AUTHORITATIVE_MEMBERSHIP_EVENT_TYPES.has(eventType))
    || (Array.isArray(patch?.ops) && patch.ops.some(
      operation => String(operation?.op ?? operation?.type ?? '').toLowerCase() === 'remove',
    ));
};

export const sportsnewMembershipPatchRequiresFailClosedHold = (patch = {}) => {
  if (normalizedEventTypes(patch).some(eventType => FAIL_CLOSED_HOLD_EVENT_TYPES.has(eventType))) {
    return true;
  }
  if (Array.isArray(patch?.ops) && patch.ops.some(
    operation => String(operation?.op ?? operation?.type ?? '').toLowerCase() === 'remove',
  )) return true;
  const status = patch?.fixture?.status ?? patch?.status;
  return TERMINAL_FIXTURE_STATUSES.has(String(status ?? '').trim().toUpperCase());
};

export const sportsnewMembershipPatchProvidesReopenAuthority = (patch = {}) => {
  if (normalizedEventTypes(patch).some(eventType => REOPEN_AUTHORITY_EVENT_TYPES.has(eventType))) {
    return true;
  }
  const membershipEnvelope = normalizedEventTypes(patch).some(eventType => (
    eventType === 'FIXTURE_LIST_MEMBERSHIP' || eventType === 'LIST_MEMBERSHIP'
  ));
  return membershipEnvelope && Array.isArray(patch?.ops) && patch.ops.some(
    operation => String(operation?.op ?? operation?.type ?? '').toLowerCase() === 'upsert',
  );
};

const fixtureId = value => {
  const id = value?.fixtureId ?? value?.fixture_id ?? value?.id;
  return id == null ? null : String(id);
};

export const sportsnewFixturePatchHasMembershipTransition = ({
  fixtures = [],
  patch,
  fallbackFixtureId = null,
  mode,
} = {}) => {
  if (!Array.isArray(fixtures) || !patch) return false;
  const projected = applyFixturePatchToFixtureList(fixtures, patch, fallbackFixtureId);
  const visibleIds = rows => new Set(rows
    .filter(fixture => isSportsnewFixtureVisibleForMode(fixture, mode))
    .map(fixtureId)
    .filter(Boolean));
  const before = visibleIds(fixtures);
  const after = visibleIds(projected);
  return before.size !== after.size || [...before].some(id => !after.has(id));
};

export async function resolveSportsnewMembershipRevisionFence({
  listRevision,
  navigationResponse,
  refreshList,
  refreshNavigation,
} = {}) {
  const initialNavigationRevision = normalizeSportsnewMembershipRevision(navigationResponse);
  if (sportsnewMembershipRevisionsCompatible(listRevision, initialNavigationRevision)) {
    return Object.freeze({
      compatible: true,
      retried: false,
      response: navigationResponse,
      listRevision: normalizeSportsnewMembershipRevision({ membershipRevision: listRevision }),
      navigationRevision: initialNavigationRevision,
    });
  }

  let refreshedList = null;
  let refreshedNavigation = null;
  try {
    refreshedList = typeof refreshList === 'function' ? await refreshList() : null;
    refreshedNavigation = typeof refreshNavigation === 'function'
      ? await refreshNavigation() : null;
  } catch {
    return Object.freeze({
      compatible: false,
      retried: true,
      response: null,
      listRevision: normalizeSportsnewMembershipRevision(refreshedList)
        ?? normalizeSportsnewMembershipRevision({ membershipRevision: listRevision }),
      navigationRevision: null,
    });
  }

  const nextListRevision = normalizeSportsnewMembershipRevision(refreshedList)
    ?? normalizeSportsnewMembershipRevision({ membershipRevision: listRevision });
  const nextNavigationRevision = normalizeSportsnewMembershipRevision(refreshedNavigation);
  // This branch is reached only after two known revisions disagreed.  A
  // missing retry response/revision is therefore a failed recovery, not an
  // old-API compatibility case.
  const compatible = refreshedNavigation != null
    && nextListRevision != null
    && nextNavigationRevision != null
    && nextListRevision === nextNavigationRevision;
  return Object.freeze({
    compatible,
    retried: true,
    response: compatible ? refreshedNavigation : null,
    listRevision: nextListRevision,
    navigationRevision: nextNavigationRevision,
  });
}

export default Object.freeze({
  normalizeSportsnewMembershipRevision,
  resolveSportsnewMembershipRevisionFence,
  sportsnewFixturePatchHasMembershipTransition,
  sportsnewListPatchHasMembershipTransition,
  sportsnewMembershipPatchProvidesReopenAuthority,
  sportsnewMembershipPatchRequiresFailClosedHold,
  sportsnewMembershipRevisionsCompatible,
});
